import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { stripeConnectViewModel } from '../../view-model/professional/stripe-connect-view-model';
import { useProfessionalViewModel } from '../../view-model/professional/professional-view-model';
import StripeConnectStatus from '../../components/stripe/stripe-connect-status';
import OnboardingButton from '../../components/stripe/onboarding-button';
import { Card, CardHeader, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import Switch from '../../components/ui/switch';
import professionalStore from '../../store/professional-store';
import subscriptionStore from '../../store/subscription-store';
import {
  Loader2,
  ExternalLink,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  CreditCard,
  Banknote,
  FileCheck,
  Info,
  Lock,
  TrendingUp,
  Receipt,
  DollarSign
} from 'lucide-react';

const StripeConnect: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'fr';
  const professionalViewModel = useProfessionalViewModel();
  const [showFeesInfo, setShowFeesInfo] = useState(false);
  const [isUpdatingFees, setIsUpdatingFees] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month' | 'all'>('day');

  const hasOnlinePayment = subscriptionStore.currentSubscription?.onlinePayment ?? false;

  useEffect(() => {
    if (hasOnlinePayment) {
      stripeConnectViewModel.initialize();
    }
  }, [hasOnlinePayment]);

  useEffect(() => {
    if (hasOnlinePayment && stripeConnectViewModel.stripeStatus?.isConnected) {
      stripeConnectViewModel.fetchRevenueStats(selectedPeriod);
    }
  }, [selectedPeriod, hasOnlinePayment]);

  const handleToggleAbsorbFees = async () => {
    setIsUpdatingFees(true);
    try {
      const newValue = !professionalStore.profileProfessional?.absorbStripeFees;
      await professionalViewModel.updateAbsorbStripeFees(newValue);
    } catch (error) {
      console.error('Error updating absorbStripeFees:', error);
    } finally {
      setIsUpdatingFees(false);
    }
  };

  // Si l'utilisateur n'a pas accès à cette fonctionnalité, afficher un message de blocage
  if (!hasOnlinePayment) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-amber-600 dark:text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {t('subscription.featureLocked', 'Fonctionnalité Premium')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('subscription.stripeConnectLockedMessage', 'Les paiements en ligne nécessitent un abonnement Premium ou Premium+.')}
          </p>
          <button
            onClick={() => navigate(`/${currentLang}/provider/subscription`)}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            {t('subscription.upgrade', 'Mettre à niveau')}
          </button>
        </div>
      </div>
    );
  }

  // Afficher un loader pendant le chargement initial
  if (stripeConnectViewModel.isLoading && !stripeConnectViewModel.stripeStatus) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary dark:text-primary-light mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('stripe.loading')}</p>
        </div>
      </div>
    );
  }

  const { stripeStatus } = stripeConnectViewModel;

  return (
    <div className="h-full flex flex-col space-y-6 overflow-auto pb-6">
      {/* En-tête */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('stripe.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('stripe.description')}
          </p>
        </div>
        <button
          onClick={() => setShowFeesInfo(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
        >
          <Info className="w-5 h-5" />
          <span className="text-sm font-medium">{t('stripe.fees.title')}</span>
        </button>
      </div>

      {/* Carte de statut principal */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('stripe.accountStatus')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('stripe.accountStatusDescription')}
              </p>
            </div>
            <StripeConnectStatus status={stripeConnectViewModel.statusBadge} />
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            {/* Statut du compte */}
            {stripeStatus && stripeStatus.isConnected ? (
              <>
                {/* Capacités du compte */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Accepter les paiements */}
                  <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    {stripeStatus.chargesEnabled ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('stripe.capabilities.charges')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {stripeStatus.chargesEnabled
                          ? t('stripe.capabilities.chargesEnabled')
                          : t('stripe.capabilities.chargesDisabled')}
                      </p>
                    </div>
                  </div>

                  {/* Recevoir des virements */}
                  <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    {stripeStatus.payoutsEnabled ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('stripe.capabilities.payouts')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {stripeStatus.payoutsEnabled
                          ? t('stripe.capabilities.payoutsEnabled')
                          : t('stripe.capabilities.payoutsDisabled')}
                      </p>
                    </div>
                  </div>

                  {/* Informations soumises */}
                  <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    {stripeStatus.detailsSubmitted ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('stripe.capabilities.details')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {stripeStatus.detailsSubmitted
                          ? t('stripe.capabilities.detailsSubmitted')
                          : t('stripe.capabilities.detailsNotSubmitted')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Informations requises */}
                {(stripeStatus.currentlyDue.length > 0 ||
                  stripeStatus.eventuallyDue.length > 0 ||
                  stripeStatus.pastDue.length > 0) && (
                  <div className="space-y-3">
                    {stripeStatus.pastDue.length > 0 && (
                      <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                        <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-900 dark:text-red-300">
                            {t('stripe.requirements.pastDue')}
                          </p>
                          <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                            {t('stripe.requirements.pastDueDescription')}
                          </p>
                        </div>
                      </div>
                    )}

                    {stripeStatus.currentlyDue.length > 0 && (
                      <div className="flex items-start gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl">
                        <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-orange-900 dark:text-orange-300">
                            {t('stripe.requirements.currentlyDue')}
                          </p>
                          <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                            {t('stripe.requirements.currentlyDueDescription')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Configuration des frais de service */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        Gestion des frais de transaction
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {professionalStore.profileProfessional?.absorbStripeFees
                          ? "Vous absorbez les frais de service. Vos clients paient uniquement le prix du service."
                          : "Les frais de service sont répercutés aux clients de manière transparente. Vous recevez 100% du prix de vos services."}
                      </p>
                    </div>
                    <Switch
                      id="absorb-stripe-fees"
                      name="absorbStripeFees"
                      label={professionalStore.profileProfessional?.absorbStripeFees ? "J'absorbe les frais" : "Répercuter aux clients"}
                      defaultChecked={professionalStore.profileProfessional?.absorbStripeFees ?? true}
                      disabled={isUpdatingFees}
                      onChange={handleToggleAbsorbFees}
                      labelClassName="text-sm font-medium text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {!stripeStatus.detailsSubmitted && (
                    <OnboardingButton
                      onClick={stripeConnectViewModel.startOnboarding}
                      isLoading={stripeConnectViewModel.isLoading}
                      isConfigured={true}
                      className="flex-1"
                    />
                  )}

                  <Button
                    onClick={stripeConnectViewModel.openDashboard}
                    disabled={stripeConnectViewModel.isLoading}
                    variant="outline"
                    className="flex-1"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('stripe.button.openDashboard')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Pas encore configuré */}
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('stripe.notConfigured.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    {t('stripe.notConfigured.description')}
                  </p>
                  <OnboardingButton
                    onClick={stripeConnectViewModel.startOnboarding}
                    isLoading={stripeConnectViewModel.isLoading}
                    isConfigured={false}
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Statistiques de revenus */}
      {stripeStatus && stripeStatus.isConnected && stripeConnectViewModel.revenueStats && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('stripe.revenueStats.title', 'Statistiques de revenus')}
            </h2>

            {/* Sélecteur de période */}
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
              <button
                onClick={() => setSelectedPeriod('day')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedPeriod === 'day'
                    ? 'bg-white dark:bg-gray-700 text-primary dark:text-primary-light shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Aujourd'hui
              </button>
              <button
                onClick={() => setSelectedPeriod('week')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedPeriod === 'week'
                    ? 'bg-white dark:bg-gray-700 text-primary dark:text-primary-light shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Cette semaine
              </button>
              <button
                onClick={() => setSelectedPeriod('month')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedPeriod === 'month'
                    ? 'bg-white dark:bg-gray-700 text-primary dark:text-primary-light shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Ce mois
              </button>
              <button
                onClick={() => setSelectedPeriod('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedPeriod === 'all'
                    ? 'bg-white dark:bg-gray-700 text-primary dark:text-primary-light shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                Tout
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total des revenus */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('stripe.revenueStats.totalRevenue', 'Revenus totaux')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stripeConnectViewModel.formatAmount(stripeConnectViewModel.revenueStats.totalRevenue)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Nombre de transactions */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('stripe.revenueStats.totalTransactions', 'Nombre de paiements')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stripeConnectViewModel.revenueStats.totalTransactions}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                    <Receipt className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Montant moyen */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('stripe.revenueStats.averageTransaction', 'Montant moyen')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stripeConnectViewModel.formatAmount(stripeConnectViewModel.revenueStats.averageTransaction)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Frais de service totaux */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('stripe.revenueStats.totalStripeFees', 'Frais de service')}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stripeConnectViewModel.formatAmount(stripeConnectViewModel.totalStripeFees)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                    <Banknote className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Modal des frais de transaction */}
      {showFeesInfo && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
            onClick={() => setShowFeesInfo(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              {/* En-tête du modal */}
              <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 rounded-t-2xl">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                      <Info className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t('stripe.fees.title')}
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('stripe.fees.description')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowFeesInfo(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <XCircle className="w-6 h-6 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Contenu du modal */}
              <div className="p-6 space-y-6">
                {/* Deux modes de gestion */}
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    {t('stripe.fees.modes', 'Modes de gestion des frais')}
                  </h3>

                  {/* Mode 1: Absorber les frais */}
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        {t('stripe.fees.mode1Title', 'J\'absorbe les frais de service')}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {t('stripe.fees.mode1Description', 'Le client paie uniquement le prix du service. Les frais de service (~1,5% + 0,25€) sont déduits de votre revenu.')}
                      </p>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Service</span>
                          <span className="font-medium">50,00 €</span>
                        </div>
                        <div className="flex justify-between text-blue-600 dark:text-blue-400">
                          <span>Client paie</span>
                          <span className="font-bold">50,00 €</span>
                        </div>
                        <div className="flex justify-between text-gray-500 dark:text-gray-500 text-[11px]">
                          <span>Frais de service déduits</span>
                          <span>- 1,00 €</span>
                        </div>
                        <div className="flex justify-between text-green-600 dark:text-green-400 pt-1 border-t border-gray-200 dark:border-gray-700">
                          <span>Vous recevez</span>
                          <span className="font-bold">≈ 49,00 €</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mode 2: Répercuter les frais */}
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-green-500 dark:bg-green-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                        {t('stripe.fees.mode2Title', 'Je répercute les frais au client')}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {t('stripe.fees.mode2Description', 'Un markup de 2% est ajouté au prix pour couvrir les frais de service. Vous recevez 100% du prix du service.')}
                      </p>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Service</span>
                          <span className="font-medium">50,00 €</span>
                        </div>
                        <div className="flex justify-between text-gray-500 dark:text-gray-500 text-[11px]">
                          <span>Markup 2%</span>
                          <span>+ 1,00 €</span>
                        </div>
                        <div className="flex justify-between text-blue-600 dark:text-blue-400 pt-1 border-t border-gray-200 dark:border-gray-700">
                          <span>Client paie</span>
                          <span className="font-bold">51,00 €</span>
                        </div>
                        <div className="flex justify-between text-green-600 dark:text-green-400">
                          <span>Vous recevez</span>
                          <span className="font-bold">50,00 €</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Commission plateforme */}
                <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-primary/5 to-purple-500/5 dark:from-primary-dark/10 dark:to-purple-900/10 rounded-xl">
                  <FileCheck className="w-5 h-5 text-primary dark:text-primary-light mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('stripe.fees.platform')}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {t('stripe.fees.platformDescription')}
                    </p>
                  </div>
                </div>

                {/* Note importante */}
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                  <p className="text-sm text-amber-900 dark:text-amber-300">
                    ⚡ <strong>{t('stripe.fees.changeSetting', 'Changez de mode')} :</strong> {t('stripe.fees.changeSettingDescription', 'Vous pouvez basculer entre les deux modes à tout moment via le toggle "Gestion des frais de transaction" sur cette page.')}
                  </p>
                </div>
              </div>

              {/* Footer du modal */}
              <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 p-4 rounded-b-2xl">
                <Button
                  onClick={() => setShowFeesInfo(false)}
                  className="w-full"
                >
                  {t('common.close', 'Fermer')}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default StripeConnect;
